import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import UIButton from 'Components/UIComponents/UIButton';
import UICheckbox from 'Components/UIComponents/UICheckbox';
import UISelect from 'Components/UIComponents/UISelect';
import UITextArea from 'Components/UIComponents/UITextArea';
import UITextInput from 'Components/UIComponents/UITextInput';
import { useCaretPreservingChange } from 'Hooks/Common';
import { useInteractMedia } from 'Hooks/Document';
import { useDocumentFieldSignerChange } from 'Hooks/DocumentFields';
import { DeleteDocumentField } from 'Hooks/DocumentFields/useDocumentFieldDelete';
import { UpdateDocumentField } from 'Hooks/DocumentFields/useDocumentFieldUpdate';
import { SelectableOption } from 'Interfaces/Common';
import { DocumentTypes, Signer } from 'Interfaces/Document';
import {
  DocumentField,
  DocumentFieldsCRUDMeta,
  DocumentFieldTypes,
} from 'Interfaces/DocumentFields';
import { fontFamilyByValue, interactInitialFont } from 'Services/Fonts';
import { checkIfDateOrText } from 'Utils/functions';
import { selectDocument, selectDocumentSigner } from 'Utils/selectors';

import FontFamilySelect from './FontFamilySelect';
import FontSizeSelect from './FontSizeSelect';
import InteractSelectItemTitle from './InteractSelectItemTitle';
import RemoveFieldButton from './RemoveFieldButton';

interface FieldItemOptionsProps {
  field: DocumentField;
  partisipantOptions: SelectableOption<string>[];
  showTitle?: boolean;
  showRemoveFieldButton?: boolean;
  showCancelButton?: boolean;
  onClose?: () => void;
  onUpdate: UpdateDocumentField;
  onChangeSigner: (signerId: string) => void;
  onDelete: DeleteDocumentField;
  onStartInputTyping: () => void;
  onStopInputTyping: () => void;
  onApplyFontToEachField: (fontFamily: string, fontSize: number) => void;
  validateTag: (id: string, tag: string) => boolean;
}

const FieldItemOptions = ({
  field,
  partisipantOptions,
  showTitle = true,
  showRemoveFieldButton,
  showCancelButton,
  onClose,
  onUpdate,
  onChangeSigner,
  onDelete,
  onStartInputTyping,
  onStopInputTyping,
  onApplyFontToEachField,
  validateTag,
}: FieldItemOptionsProps) => {
  const {
    id,
    documentId,
    signerId,
    type,
    required,
    checked,
    minimizeWidth,
    fontFamily,
    fontSize,
    placeholder,
    tag,
  } = field;
  const [isTagInvalid, setIsTagInvalid] = useState(false);
  const [isExtraMobile, isTablet] = useInteractMedia(['xs', 'lg']);
  const getFieldSignerUpdateData = useDocumentFieldSignerChange(field);
  const document = useSelector(state => selectDocument(state, { documentId }));
  const signer = useSelector(state =>
    selectDocumentSigner(state, { documentId, signerId }),
  ) as Signer;
  const isDateOrText = useMemo(() => checkIfDateOrText(type), [type]);

  const removeCurrentField = useCallback(() => {
    onDelete({ id });
  }, [id, onDelete]);

  const changeCurrentFieldData = useCallback(
    (data: Partial<DocumentField>, meta?: DocumentFieldsCRUDMeta) => {
      onUpdate({ id, ...data }, meta);
    },
    [id, onUpdate],
  );

  const handleFieldRequiretyChange = useCallback(
    () => changeCurrentFieldData({ required: !required }),
    [required, changeCurrentFieldData],
  );

  const handleFieldCheckedChange = useCallback(() => {
    changeCurrentFieldData({ checked: !checked });
  }, [checked, changeCurrentFieldData]);

  const handleFieldMinimizeWidthChange = useCallback(() => {
    changeCurrentFieldData({ minimizeWidth: !minimizeWidth });
  }, [minimizeWidth, changeCurrentFieldData]);

  const handleTagFieldChange = useCallback(
    (tag: string) => {
      changeCurrentFieldData({ tag });
      setIsTagInvalid(!validateTag(id, tag));
    },
    [id, validateTag, changeCurrentFieldData],
  );

  const changeFontFamily = useCallback(
    fontFamily => {
      if (!fontFamily) return;
      changeCurrentFieldData({
        fontFamily,
        style: {
          fontFamily: fontFamilyByValue[fontFamily],
        },
      });
    },
    [changeCurrentFieldData],
  );

  const changeFontSize = useCallback(
    fontSize => {
      if (!fontSize) return;
      changeCurrentFieldData(
        {
          fontSize,
          fixedFontSize: true,
          style: {
            fontSize,
          },
        },
        { stopFontAutoSize: true, pushToHistory: true },
      );
    },
    [changeCurrentFieldData],
  );

  const handlePlaceholderChange = useCallback(
    newPlaceholderValue => {
      changeCurrentFieldData({ placeholder: newPlaceholderValue });
    },
    [changeCurrentFieldData],
  );

  const handleSignerSelect = useCallback(
    signerId => {
      const updateData = getFieldSignerUpdateData(signerId);
      onChangeSigner(signerId);
      if (updateData) changeCurrentFieldData(updateData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changeCurrentFieldData, getFieldSignerUpdateData],
  );

  const showTagHint = tag && tag.length > 0;

  const handleApplyToDocumentButton = () => {
    onApplyFontToEachField(
      fontFamily || interactInitialFont.fontFamily,
      fontSize || interactInitialFont.fontSize,
    );
  };

  const {
    inputRef: labelRef,
    handleChange: handleLabelChange,
  } = useCaretPreservingChange<HTMLTextAreaElement>({
    onValueChange: newPlaceholderValue => {
      handlePlaceholderChange(newPlaceholderValue);
    },
  });

  return (
    <li className="interactModal__fieldBar-selectField-item-wrapper">
      {showTitle && (
        <InteractSelectItemTitle
          fieldType={type}
          showRemoveFieldButton={showRemoveFieldButton}
          onRemoveField={removeCurrentField}
        />
      )}
      <div className="interactModal__fieldBar-selectField-item-content">
        <div className="interactModal__fieldBar-selectField-selector-wrapper">
          <p className="interactModal__fieldBar-selectField-selector-title">
            Assigned To:
          </p>
          <UISelect
            value={signerId}
            handleSelect={handleSignerSelect}
            options={partisipantOptions}
            placeholder="Select Recipient"
          />
        </div>
        {isDateOrText && (
          <div className="interactModal__fieldBar-selectField-selector-fontWrapper">
            <div className="interactModal__fieldBar-selectField-selector-wrapper">
              <p className="interactModal__fieldBar-selectField-selector-title">
                Font Family:&nbsp;
              </p>
              <FontFamilySelect value={fontFamily} onSelect={changeFontFamily} />
            </div>
            <div className="interactModal__fieldBar-selectField-selector-wrapper">
              <p className="interactModal__fieldBar-selectField-selector-title">
                Font Size:&nbsp;
              </p>
              <FontSizeSelect onSelect={changeFontSize} value={fontSize || null} />
            </div>
          </div>
        )}
        {type === DocumentFieldTypes.Text && !signer?.isPreparer && (
          <div className="interactModal__fieldBar-selectField-inputField">
            <p className="interactModal__fieldBar-selectField-inputField-title">Label</p>
            <UITextArea
              ref={labelRef}
              value={placeholder}
              placeholder="Enter your custom label here"
              onChange={handleLabelChange}
              onFocus={onStartInputTyping}
              onBlur={onStopInputTyping}
            />
          </div>
        )}
        <div className="interactModal__fieldBar-selectField-options">
          <div className="interactModal__fieldBar-selectField-checkbox">
            {type !== DocumentFieldTypes.Name && (
              <UICheckbox
                handleClick={handleFieldRequiretyChange}
                check={required}
                label="Required"
              />
            )}
            {type === DocumentFieldTypes.Checkbox && !signer?.isPreparer && (
              <UICheckbox
                handleClick={handleFieldCheckedChange}
                check={!!checked}
                label="Checked by default"
              />
            )}
            {type === DocumentFieldTypes.Initials && signer?.isPreparer && (
              <UICheckbox
                handleClick={handleFieldMinimizeWidthChange}
                check={!!minimizeWidth}
                label="Minimize width"
              />
            )}
          </div>
          {isTablet && !isExtraMobile && (
            <RemoveFieldButton onClick={removeCurrentField} variant="icon-text" />
          )}
        </div>
        {type !== DocumentFieldTypes.Signature &&
          type !== DocumentFieldTypes.Initials &&
          document?.type === DocumentTypes.TEMPLATE && (
            <div className="interactModal__fieldBar-selectField-inputField">
              <p className="interactModal__fieldBar-selectField-inputField-title">Tag</p>
              <UITextInput
                value={tag || ''}
                placeholder="Tag"
                error={isTagInvalid}
                onChange={event => handleTagFieldChange(event.target.value)}
                onFocus={onStartInputTyping}
                onBlur={onStopInputTyping}
              />
              {showTagHint && (
                <p className="interactModal__fieldBar-selectField-inputField-hint">
                  We recommend you to adjust font size and field size with example value
                  in placeholder. When you&apos;re using API to fill this fields, we
                  don&apos;t automatically adjust font size or field size.
                </p>
              )}
            </div>
          )}
      </div>
      {isDateOrText && (
        <div className="interactModal__fieldBar-selectField-selector-apply-wrapper">
          {showCancelButton && (
            <UIButton
              priority="white"
              className="centered-text interactModal__fieldBar-selectField-selector-apply interactModal__fieldBar-selectField-selector-apply--cancel"
              title="Cancel"
              handleClick={onClose}
            />
          )}
          <UIButton
            priority="primary"
            className="centered-text interactModal__fieldBar-selectField-selector-apply"
            title="Apply to Document"
            handleClick={handleApplyToDocumentButton}
          />
        </div>
      )}
    </li>
  );
};

export default FieldItemOptions;
